// apps/client/src/world/objectView.ts (M0-T13) — contract for the world/objects registry (§8.4)
import { getEntry, type CatalogId, type ObjectKind } from '@whiskerton/content';
import { rotateFootprint, type PrivateState, type ResidentId, type ResidentPublic, type TownInfo, type Vec2, type WorldObject } from '@whiskerton/shared';
import * as THREE from 'three';
import type { AssetService } from '../assets/service';

export interface ViewCtx { assets: AssetService; town: TownInfo; residents: Readonly<Record<ResidentId, ResidentPublic>>; you: PrivateState | null; now: number; heightAt(p: Vec2): number }
export interface ObjectView {
  kinds: readonly ObjectKind[];
  build(obj: WorldObject, ctx: ViewCtx): Promise<THREE.Object3D>;
  update?(obj: WorldObject, view: THREE.Object3D, ctx: ViewCtx): void;   // state or your private state changed
  perResident?: boolean;                                                 // re-run update on `you` changes (fruit visibility)
}
/** rev 7: returned by ObjectView.build to join a shared instanced batch (same key → one InstancedMesh per sub-mesh of template). */
export class InstanceProxy extends THREE.Object3D {
  readonly key: string;
  readonly template: THREE.Object3D;
  constructor(key: string, template: THREE.Object3D) { super(); this.key = key; this.template = template; this.userData.instanced = true; }
}
/** Trees and rocks (2026-10-16): marks `o`, a child of a template, as the template's far model and hides it. For an instance at least
 *  tuning.render.farModelFrom from the camera the outdoor instancer draws the far model's meshes instead of the rest of the template;
 *  a template without one draws all of itself at every distance. Anything that shows the template itself never sees the hidden child. */
export function farModel<T extends THREE.Object3D>(o: T): T { o.userData.lod = 'far'; o.visible = false; return o; }
/** Whether `o` (or a parent of it, up to `root`) is a template's far model. */
export function inFarModel(o: THREE.Object3D, root: THREE.Object3D | null = null): boolean {
  for (let p: THREE.Object3D | null = o; p && p !== root; p = p.parent) if (p.userData.lod === 'far') return true;
  return false;
}
const templates = new WeakMap<AssetService, Map<string, Promise<THREE.Object3D>>>();
/** One model per asset service and catalog id, shared as the template of every InstanceProxy of that id (never added to a scene). */
export function modelTemplate(assets: AssetService, id: CatalogId): Promise<THREE.Object3D> {
  let byId = templates.get(assets);
  if (!byId) templates.set(assets, (byId = new Map()));
  let p = byId.get(id);
  if (!p) { p = assets.model(id); byId.set(id, p); p.catch(() => byId.delete(id)); }
  return p;
}
/** Centre of the rotated footprint at ground height, turned by `rot`. */
export function placeOnTile(o: THREE.Object3D, obj: Pick<WorldObject, 'x' | 'y' | 'rot' | 'catalogId'>, heightAt: (p: Vec2) => number): void {
  const e = getEntry(obj.catalogId);
  const { w, d } = rotateFootprint(e && e.kind === 'world' ? e.footprint : [1, 1], obj.rot);
  const c = { x: obj.x + w / 2, y: obj.y + d / 2 };
  o.position.set(c.x, heightAt(c), c.y);
  o.rotation.y = -obj.rot * (Math.PI / 2);   // quarter turns clockwise seen from above; 0 faces +y (D-07)
}
